'use client';

import { useMemo, useState } from 'react';
import SpinePlayer from './spine-player';
import {
  LostSwordDialoguePanel,
  LostSwordVoicePanel,
} from './lostsword-archive-media';
import type { HeroData, LostSwordSkillLine } from '../lib/heroes/types';
import { staticUrl } from '../lib/config/static-assets';
import './lostsword-hero-content.css';

const UI = staticUrl('/assets/lostsword-ui');

const ACTION_TAB_MAP: Record<string, string> = {
  Idle: '待机',
  Run: '奔跑',
  Walk: '行走',
  Die: '阵亡',
  Win: '结算',
  Attack: '普通攻击',
  Skill: '主动技能',
  Ultimate: '终结技能',
  Pain: '受击',
  Pinch: '束缚',
  Stun: '眩晕',
};

function mapActionTabs(animations: string[]) {
  const matched: { id: string; label: string }[] = [];
  for (const key of Object.keys(ACTION_TAB_MAP)) {
    const found = animations.find((name) =>
      name.toLowerCase().startsWith(key.toLowerCase()),
    );
    if (found) matched.push({ id: found, label: ACTION_TAB_MAP[key] });
  }
  if (matched.length) return matched;
  return animations.slice(0, 12).map((id) => ({ id, label: id }));
}

const TALENT_FRAME_MAP: Record<string, number[]> = {
  'talent-u-1.png': [1, 2],
  'talent-u-2.png': [3, 4],
  'talent-u-3.png': [5],
  'talent-u-4.png': [6],
};

function themeClass(theme: string) {
  if (theme === 'frost') return 'ls-v4-theme-ice';
  if (theme === 'light') return 'ls-v4-theme-light';
  return 'ls-v4-theme-neutral';
}

function talentFrame(index: number) {
  const n = index + 1;
  for (const [file, nums] of Object.entries(TALENT_FRAME_MAP)) {
    if (nums.includes(n)) return `${UI}/${file}`;
  }
  return `${UI}/talent-u-1.png`;
}

function highlightNums(text: string) {
  const parts = String(text).split(/(\d+(?:\.\d+)?%?)/g);
  return parts.map((part, index) =>
    /^\d+(?:\.\d+)?%?$/.test(part) ? (
      <span className="num" key={`${part}-${index}`}>
        {part}
      </span>
    ) : (
      <span key={`${part}-${index}`}>{part}</span>
    ),
  );
}

function SkillLines({ lines }: { lines: LostSwordSkillLine[] }) {
  return (
    <div className="ls-v4-skill-body">
      {lines.map((line, index) => {
        if (line.kind === 'lab') {
          return (
            <div key={index}>
              <span className="lab">{line.lab}</span>
              {highlightNums(line.text)}
              {line.num ? <span className="num">{line.num}</span> : null}
              {line.suffix ? highlightNums(line.suffix) : null}
            </div>
          );
        }
        if (line.kind === 'strong') {
          const match = line.text.match(/^(【[^】]+】)(.*)$/);
          if (match) {
            return (
              <div key={index}>
                <strong>{match[1]}</strong>
                {highlightNums(match[2])}
              </div>
            );
          }
          return (
            <div key={index}>
              <strong>{line.text}</strong>
            </div>
          );
        }
        return <div key={index}>{highlightNums(line.text)}</div>;
      })}
    </div>
  );
}

export default function LostSwordHeroContent({ hero }: { hero: HeroData }) {
  const detail = hero.lostsword;
  const combatSkills = detail?.combatSkills?.length
    ? detail.combatSkills
    : hero.skills.map((skill) => ({
        id: skill.id,
        name: skill.name,
        type: skill.type,
        icon: skill.icon,
        levels: skill.ranks.map((rank) => ({
          lines: [{ kind: 'text' as const, text: rank }],
        })),
      }));

  const [skillLevels, setSkillLevels] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      combatSkills.map((skill) => [skill.id, Math.max(1, skill.levels.length)]),
    ),
  );

  const voiceGroups = useMemo(() => {
    const map = new Map<string, { text: string; src?: string }[]>();
    for (const voice of detail?.voices || []) {
      const list = map.get(voice.label) || [];
      list.push({ text: voice.text, src: voice.src });
      map.set(voice.label, list);
    }
    return [...map.entries()];
  }, [detail?.voices]);

  const sdForms = hero.forms.filter((form) => form.sd);
  const [skinIndex, setSkinIndex] = useState(0);
  const activeSdForm =
    sdForms[skinIndex] || sdForms[0] || hero.forms.find((form) => form.sd) || hero.forms[0];
  const sdForm = activeSdForm;
  const [sdAction, setSdAction] = useState('Idle');
  const [sdActions, setSdActions] = useState(sdForm?.actions || []);

  const resonance = detail?.resonance;
  const hasGrowth =
    Boolean(resonance) ||
    (detail?.transcendence?.length ?? 0) > 0 ||
    (detail?.practice?.length ?? 0) > 0;

  const displaySkins = (() => {
    if (!sdForms.length) return [];
    const current = sdForms[skinIndex] || sdForms[0];
    if (sdForms.length < 2) return [current];
    const next = sdForms[(skinIndex + 1) % sdForms.length];
    return [current, next];
  })();

  const handleSkinClick = (displayIndex: number) => {
    if (displayIndex === 0 || sdForms.length < 2) return;
    const nextIndex = (skinIndex + 1) % sdForms.length;
    setSkinIndex(nextIndex);
    setSdActions(sdForms[nextIndex]?.actions || []);
    setSdAction('Idle');
  };

  return (
    <div className={`ls-v4 ls-v4--content-only is-content-active ${themeClass(hero.theme)}`}>
      <div className="ls-v4-paper">
        <div className="ls-v4-panel">
          {/* CombatKit */}
          <section
            id="skills"
            className="ls-v4-section"
            aria-label="技能属性"
            data-ls-section="detail"
          >
            <header className="ls-v4-section-head">
              <p className="ls-v4-eyebrow">COMBAT KIT</p>
              <h2>技能属性</h2>
            </header>
            {combatSkills.length ? (
              <div className="ls-v4-skill-list">
                {combatSkills.map((skill) => {
                  const level = skillLevels[skill.id] || skill.levels.length;
                  const active = skill.levels[level - 1] || skill.levels[0];
                  return (
                    <article className="ls-v4-skill" key={skill.id}>
                      <div className="ls-v4-skill-icon">
                        {skill.icon ? <img src={skill.icon} alt={skill.name} /> : null}
                      </div>
                      <div className="ls-v4-skill-main">
                        <div className="ls-v4-skill-title-row">
                          <h3>{skill.name}</h3>
                          <div className="ls-v4-skill-meta">
                            {skill.type ? <span>{skill.type}</span> : null}
                          </div>
                        </div>
                        {skill.levels.length > 1 ? (
                          <div
                            className="ls-v4-level-tabs"
                            role="tablist"
                            aria-label={`${skill.name}等级`}
                          >
                            {skill.levels.map((_, index) => {
                              const lv = index + 1;
                              return (
                                <button
                                  key={lv}
                                  type="button"
                                  role="tab"
                                  aria-selected={level === lv}
                                  className={level === lv ? 'is-active' : ''}
                                  onClick={() =>
                                    setSkillLevels((prev) => ({
                                      ...prev,
                                      [skill.id]: lv,
                                    }))
                                  }
                                >
                                  {`Lv.${lv}`}
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                        {active ? <SkillLines lines={active.lines} /> : (
                          <div className="ls-v4-skill-body">
                            <div>暂无等级效果</div>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="ls-v4-empty">暂无技能数据</div>
            )}
          </section>

          {/* GrowthBlock */}
          {hasGrowth ? (
            <section id="ls-v4-growth" className="ls-v4-section" aria-label="养成成长">
              <header className="ls-v4-section-head">
                <p className="ls-v4-eyebrow">GROWTH</p>
                <h2>养成成长</h2>
              </header>

              {resonance ? (
                <div id="ls-v4-card" className="ls-v4-archive-block">
                  <h3 className="ls-v4-subhead">共鸣卡牌</h3>
                  <div className="ls-v4-embed ls-v4-embed--pad">
                    <div className="card-container">
                      <div className="first-kp">
                        <div className={`left star${Math.min(5, Math.max(3, hero.stars || 5))}`}>
                          <div className="img-wrap">
                            <img src={resonance.icon || hero.icon} alt={resonance.name} />
                          </div>
                          <div className="stars">
                            {Array.from({ length: Math.min(5, Math.max(3, hero.stars || 5)) }).map(
                              (_, i) => (
                                <img
                                  key={i}
                                  src={`${UI}/card-start.png`}
                                  className="star"
                                  alt=""
                                />
                              ),
                            )}
                          </div>
                        </div>
                        <div className="right">
                          <div className="name">{resonance.name}</div>
                          {resonance.stats.length > 0 ? (
                            <div className="full overflow-ellipsis">
                              <span className="label">满级属性：</span>
                              <span className="value">{resonance.stats.join('　')}</span>
                            </div>
                          ) : null}
                          <div className="line" />
                          {(resonance.passiveName || resonance.passiveExplain) && (
                            <>
                              <div className="passive">
                                <div className="icon-bg">
                                  <img
                                    src={hero.skills[0]?.icon || hero.head || ""}
                                    className="icon"
                                    alt=""
                                  />
                                </div>
                                <span className="name">
                                  {resonance.passiveName || resonance.name}
                                </span>
                                <span className="mark">[被动]</span>
                              </div>
                              {resonance.passiveExplain ? (
                                <div className="xiaoguo">
                                  <span className="label">满级效果：</span>
                                  <span className="value">{resonance.passiveExplain}</span>
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="ls-v4-grid-2">
                <div className="ls-v4-card">
                  <h3 className="ls-v4-subhead">超越</h3>
                  {(detail?.transcendence?.length ?? 0) > 0 ? (
                    <ol className="ls-v4-growth-list ls-v4-talent-list">
                      {detail!.transcendence.map((node, index) => (
                        <li key={node.title}>
                          <div className="ls-v4-talent-icon">
                            <div
                              className="ls-v4-talent-frame"
                              style={{
                                backgroundImage: `url(${talentFrame(index)})`,
                              }}
                            >
                              <img
                                className="ls-v4-talent-avatar"
                                src={hero.head}
                                alt=""
                              />
                            </div>
                            <img
                              className="ls-v4-talent-num"
                              src={`${UI}/talent-t-${Math.min(6, index + 1)}.png`}
                              alt={`超越${index + 1}`}
                            />
                          </div>
                          <p>{node.lines.join('\n')}</p>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="ls-v4-empty">暂无超越数据</div>
                  )}
                </div>

                <div className="ls-v4-card">
                  <h3 className="ls-v4-subhead">修炼</h3>
                  {(detail?.practice?.length ?? 0) > 0 ? (
                    <ol className="ls-v4-growth-list ls-v4-cultivate-list">
                      {detail!.practice.map((node, index) => (
                        <li key={node.title}>
                          <div className="ls-v4-cultivate-icon">
                            <img src={`${UI}/poper.png`} alt="" />
                            <span>第{index + 1}阶段</span>
                          </div>
                          <p>
                            {node.lines[0] ? <strong>{node.lines[0]}</strong> : null}
                            {node.lines.length > 1 ? ` · ${node.lines.slice(1).join(' · ')}` : null}
                          </p>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="ls-v4-empty">暂无修炼数据</div>
                  )}
                </div>
              </div>
            </section>
          ) : null}

          {/* ArchiveBlock */}
          <section
            id="archive"
            className="ls-v4-section"
            aria-label="档案资料"
            data-ls-section="archive"
          >
            <header className="ls-v4-section-head">
              <p className="ls-v4-eyebrow">CHARACTER ARCHIVE</p>
              <h2>档案资料</h2>
            </header>

            <div className="ls-v4-archive-block">
              <h3 className="ls-v4-subhead">基础信息</h3>
              {(detail?.profile?.length ?? 0) > 0 ? (
                <dl className="ls-v4-profile-grid">
                  {detail!.profile.map((item) => (
                    <div className="ls-v4-profile-cell" key={item.label}>
                      <dt>{item.label}</dt>
                      <dd>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="ls-v4-empty">暂无人设字段</div>
              )}
            </div>

            <div className="ls-v4-archive-block">
              <h3 className="ls-v4-subhead">英雄介绍</h3>
              {hero.story || detail?.storyCard ? (
                <p className="ls-v4-story">
                  {[hero.story, detail?.storyCard].filter(Boolean).join('\n\n')}
                </p>
              ) : (
                <div className="ls-v4-empty">暂无故事</div>
              )}
            </div>

            {(detail?.gifts?.length ?? 0) > 0 ? (
              <div className="ls-v4-archive-block">
                <h3 className="ls-v4-subhead">喜欢的礼物</h3>
                <div className="ls-v4-gift-grid">
                  {detail!.gifts.map((gift) => (
                    <figure key={gift.name}>
                      <div className="ls-v4-gift-icon">
                        {gift.icon ? <img src={gift.icon} alt={gift.name} /> : null}
                      </div>
                      <figcaption>{gift.name || '礼物'}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            ) : null}

            {voiceGroups.length > 0 ? (
              <div className="ls-v4-archive-block">
                <div className="ls-v4-block-head">
                  <h3 className="ls-v4-subhead">语音</h3>
                  <div className="ls-v4-lang-switch" role="group" aria-label="语音语言">
                    <button type="button" className="is-active">
                      中文
                    </button>
                  </div>
                </div>
                <div className="ls-v4-embed ls-v4-embed--pad">
                  <LostSwordVoicePanel groups={voiceGroups} />
                </div>
              </div>
            ) : null}

            {(detail?.dialogues?.length ?? 0) > 0 ? (
              <div id="ls-v4-dialogue" className="ls-v4-archive-block">
                <h3 className="ls-v4-subhead">好感度对话</h3>
                <div className="ls-v4-embed ls-v4-embed--pad">
                  <LostSwordDialoguePanel
                    groups={detail!.dialogues}
                    roleAvatar={hero.head}
                  />
                </div>
              </div>
            ) : null}

            {sdForm?.sd ? (
              <div id="ls-v4-action" className="ls-v4-archive-block">
                <h3 className="ls-v4-subhead">动作</h3>
                <div className="ls-v4-embed ls-v4-embed--action">
                  <div className="action">
                    <div className="action-left">
                      {displaySkins.map((form, index) => (
                        <div
                          key={`${form.id}-${index}`}
                          className={`action-item${index === 0 ? ' active' : ''}`}
                          onClick={() => handleSkinClick(index)}
                        >
                          <div className="item-box">
                            <img
                              src={form.head || form.fallback || hero.head || ""}
                              alt={form.name}
                              className="action-image"
                            />
                            <div className="action-info">
                              <img
                                src={form.fallback || hero.icon}
                                alt=""
                                className="action-logo"
                              />
                              <div className="action-name">
                                <div className="name">{form.name}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="action-right">
                      <div className="action-live2d">
                        <SpinePlayer
                          key={sdForm.id}
                          asset={sdForm.sd}
                          fallback={sdForm.head || hero.head}
                          title={`${hero.name} · 动作`}
                          className="ls-v4-action-stage"
                          animation={sdAction}
                          eager
                          onAnimations={(animations) => {
                            const tabs = mapActionTabs(animations);
                            setSdActions(
                              tabs.length
                                ? tabs
                                : sdForm.actions?.length
                                  ? sdForm.actions
                                  : [{ id: 'Idle', label: '待机' }],
                            );
                            setSdAction((current) => {
                              if (tabs.some((tab) => tab.id === current)) {
                                return current;
                              }
                              return tabs[0]?.id || current || 'Idle';
                            });
                          }}
                        />
                      </div>
                      <div className="scroll-wrapper" aria-label="切换动作">
                        <div className="scroll-content">
                          {sdActions.map((action) => (
                            <button
                              key={action.id}
                              type="button"
                              className={`tab-item${sdAction === action.id ? ' active' : ''}`}
                              onClick={() => setSdAction(action.id)}
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </section>

          {/* EvaluateBlock — 本地无编辑器评测稿时显示占位，结构对齐项目 */}
          <section id="ls-v4-evaluate" className="ls-v4-section" aria-label="评测攻略">
            <header className="ls-v4-section-head">
              <p className="ls-v4-eyebrow">EVALUATION</p>
              <h2>评测攻略</h2>
            </header>
            <div className="ls-v4-empty">评测内容待完善</div>
          </section>
        </div>
      </div>
    </div>
  );
}
